<template>
  <sme-card :title="$t('dashboard.shell.pill.pay')" :to="{ name: 'sme-pay-day' }" class="payschedules">
    <p>{{ $t('dashboard.pay.intro') }}</p>
    <app-loading :loading="loading" />
    <template v-if="!loading">
      <sme-alert v-if="!payschedules.length" :level="ALERT_LEVELS.DANGER">
        {{ $t('dashboard.pay.noPayscheduleAlert') }}
      </sme-alert>
      <sme-alert v-else-if="!payscheduleReports.length" :level="ALERT_LEVELS.GOOD">
        {{ $t('dashboard.pay.payscheduleAddedAlert') }}
      </sme-alert>
      <div v-else>
        <sme-info-box
          id="pay-terminology"
          :title="$t('dashboard.pay.infoBoxTitle')"
          v-if="!hideStreamAccountRelatedItems"
        >
          <h4>{{ $t('dashboard.pay.missedSalaryTitle') }}</h4>
          <i18n path="dashboard.pay.missedSalaryBody" tag="p">
            <template #label
              ><strong>{{ $t('dashboard.pay.missedSalaryLabel') }}</strong></template
            >
          </i18n>
          <h4>{{ $t('dashboard.pay.invalidBankDetailsTitle') }}</h4>
          <i18n path="dashboard.pay.invalidBankDetailsBody" tag="p">
            <template #bankDetails
              ><strong>{{ $t('dashboard.pay.invalidBankDetailsLabel') }}</strong></template
            >
            <template #portalLink>
              <router-link :to="{ name: 'employees' }" target="_blank">{{
                $t('dashboard.pay.portalLinkText')
              }}</router-link>
            </template>
          </i18n>
          <h4>{{ $t('dashboard.pay.yetToBePaidTitle') }}</h4>
          <p>{{ $t('dashboard.pay.yetToBePaidBody') }}</p>
        </sme-info-box>
        <sme-alert
          v-if="
            (hasFailedSalaryEmployees || hasInvalidBankDetailsEmployees || hasMissedSalaryEmployees) &&
            !hideStreamAccountRelatedItems &&
            !isDemoCompany
          "
          :level="ALERT_LEVELS.DANGER"
          class="mb-3"
        >
          <i18n path="dashboard.pay.issuesAlert" tag="span">
            <template #payLink
              ><sme-button-link :to="{ name: 'sme-pay-day' }">{{
                $t('dashboard.shell.pill.pay')
              }}</sme-button-link></template
            >
          </i18n>
        </sme-alert>
        <div class="payschedules__list">
          <dashboard-payschedules-tab
            v-for="data in payscheduleReports"
            :key="data.payschedule.pay_schedule_id"
            :data="data"
          />
        </div>
      </div>
    </template>

    <template v-if="!loading" v-slot:footer>
      <b-button :to="{ name: 'sme-pay-day' }" variant="outline-primary">{{
        $t('dashboard.pay.viewPayschedulesButton')
      }}</b-button>
      <b-button v-if="!payschedules.length && !isDisablePayscheduleEditing" @click="onClick" variant="primary">
        {{ $t('dashboard.pay.addPayscheduleButton') }}
      </b-button>
    </template>
  </sme-card>
</template>

<script setup>
import { computed, onBeforeMount } from 'vue';
import AppLoading from '@/components/AppLoading.vue';
import SmeAlert, { ALERT_LEVELS } from '@/components/atoms/SmeAlert';
import SmeButtonLink from '@/components/atoms/SmeButtonLink.vue';
import SmeCard from '@/components/atoms/SmeCard';
import SmeInfoBox from '@/components/atoms/SmeInfoBox';
import useFeatureFlags from '@/composables/useFeatureFlags';
import DashboardPayschedulesTab from '@/pages/dashboard/components/payschedules/DashboardPayschedulesTab';
import usePayschedulesReport from '@/state/composables/usePayschedulesReport';
import usePayschedules from '@/state/composables/usePayschedules';
import { toFormattedDate } from '@/utils/date';
import { useRouter } from 'vue-router/composables';
import useAnalytics from '@/state/composables/useAnalytics';

const { postAnalyticsEvent } = useAnalytics();
const $router = useRouter();
const {
  getPayschedulesReport,
  loading: payschedulesReportLoading,
  payscheduleReports,
  hasFailedSalaryEmployees,
  hasInvalidBankDetailsEmployees,
  hasMissedSalaryEmployees,
} = usePayschedulesReport();
const { getPayschedules, loading: loadingPayschedules, payschedules } = usePayschedules();
const { isDisablePayscheduleEditing, hideStreamAccountRelatedItems, isDemoCompany } = useFeatureFlags();

const startDate = toFormattedDate(new Date().setDate(new Date().getDate() - 35));
const endDate = toFormattedDate(new Date());

const loading = computed(() => payschedulesReportLoading.value || loadingPayschedules.value);

const onClick = () => {
  postAnalyticsEvent({
    intent: 'navigate_dashboard_pay_schedules_add_pay_schedule',
    task: 'add_pay_schedule',
    action: 'click',
  });
  $router.push({ name: 'pay-schedule', params: { payScheduleId: 'create' } });
};

onBeforeMount(async () => {
  await Promise.all([getPayschedules(), getPayschedulesReport(startDate, endDate)]);
});
</script>

<style lang="scss" scoped>
.payschedules__list {
  max-height: 25rem;
  overflow-y: auto;
}
</style>
